import { useRef, useEffect, useCallback } from 'react';

const SWIPE_THRESHOLD = 75;     // Minimum distance in px
const SWIPE_MAX_TIME = 400;     // Maximum swipe duration in ms
const EDGE_ZONE_RATIO = 0.15;   // Left 15% of screen = edge zone
const VERTICAL_TOLERANCE = 1.5;  // Horizontal distance must be > 1.5x vertical

interface TouchState {
  startX: number;
  startY: number;
  startTime: number;
  isTracking: boolean;
}

export function useSwipeNavigation(onSwipeBack: () => void) {
  const ref = useRef<HTMLElement>(null);
  const touchState = useRef<TouchState>({
    startX: 0,
    startY: 0,
    startTime: 0,
    isTracking: false,
  });

  const isTouchDevice = useCallback(() => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || !isTouchDevice()) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      const edgeZone = window.innerWidth * EDGE_ZONE_RATIO;

      // Only track swipes starting from the left edge zone
      if (touch.clientX > edgeZone) return;

      touchState.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        isTracking: true,
      };
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const state = touchState.current;
      if (!state.isTracking) return;
      state.isTracking = false;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - state.startX;
      const deltaY = Math.abs(touch.clientY - state.startY);
      const elapsed = Date.now() - state.startTime;

      // Must be a rightward swipe, exceed threshold, within time limit,
      // and more horizontal than vertical
      if (
        deltaX >= SWIPE_THRESHOLD &&
        elapsed <= SWIPE_MAX_TIME &&
        deltaX > deltaY * VERTICAL_TOLERANCE
      ) {
        onSwipeBack();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      const state = touchState.current;
      if (!state.isTracking) return;

      const touch = e.touches[0];
      const deltaY = Math.abs(touch.clientY - state.startY);
      const deltaX = touch.clientX - state.startX;

      // Cancel if scrolling vertically
      if (deltaY > 30 && deltaY > Math.abs(deltaX)) {
        state.isTracking = false;
      }
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onSwipeBack, isTouchDevice]);

  return ref;
}
